import PageShell from "@/components/PageShell";
import usePageMeta from "@/lib/usePageMeta";
import { motion } from "framer-motion";
import { cta } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const About = () => {
  usePageMeta({
    title:
      "About Postmark & Co. — Independent Direct Mail & Postcard Marketing Guide",
    description:
      "Postmark & Co. is an independent editorial site helping U.S. small businesses, realtors, and marketers grow through postcard marketing. Learn who we are, our editorial policy, and how we recommend Thanks.io as our partner postcard printing and mailing service.",
    canonical: "https://maildirect.store/about",
  });

  return (
    <PageShell testid="about-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div variants={fade} initial="hidden" animate="show">
          <div className="postmark text-[#D93829] mb-6">
            <span className="w-1 h-1 rounded-full bg-[#D93829]" />
            About
          </div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#1C1C1A]">
            An independent field guide to{" "}
            <span className="italic text-[#D93829]">
              growing a business by mail
            </span>
            .
          </h1>
        </motion.div>

        <div className="mt-14 prose-editorial space-y-8 text-[#3B3A36] text-lg leading-relaxed font-sans-body">
          <p>
            Postmark &amp; Co. is a small, independently-run editorial site
            dedicated to one idea: the mailbox is still the highest-signal
            marketing channel most modern businesses have forgotten how to use.
            Everything we publish is written to help U.S. small businesses,
            realtors, insurance agents, restaurants, retail stores, service
            businesses, nonprofits, and event marketers put a real, physical
            postcard into the right person&rsquo;s hand at the right moment.
          </p>

          <h2 className="font-serif-display text-3xl text-[#1C1C1A] pt-6">
            Why we exist
          </h2>
          <p>
            The average American adult sees roughly 4,000 to 10,000 ads per
            day online. Their inboxes are filtered by machines, their phones
            are set to Do Not Disturb, and their algorithmic feeds show them
            what a model decided they wanted rather than what a real business
            wanted to say. The mailbox is a rare quiet room. Everything that
            arrives there is opened, held, and — for a fleeting few seconds —
            actually considered. We think that&rsquo;s an unfair advantage
            worth teaching people to use.
          </p>

          <h2 className="font-serif-display text-3xl text-[#1C1C1A] pt-6">
            Our editorial standards
          </h2>
          <p>
            Every claim we make about response rates, delivery speed, or
            pricing is sourced from public U.S. Data &amp; Marketing
            Association studies, USPS operational data, or our partner&rsquo;s
            published documentation. When we cite a statistic, we link the
            source. When a comparison is our opinion, we say so. We do not
            publish auto-generated content, we do not accept undisclosed paid
            placements, and we do not sell your data.
          </p>

          <h2 className="font-serif-display text-3xl text-[#1C1C1A] pt-6">
            How we make money (affiliate disclosure)
          </h2>
          <p>
            Postmark &amp; Co. is a Thanks.io affiliate partner (partner code
            <em> ahx</em>). When a reader signs up for Thanks.io through a link
            on this site, we may earn a commission — at no additional cost to
            the reader. We only recommend Thanks.io because it is, in our
            editorial judgment, the best consumer-friendly postcard printing
            and mailing service currently operating in the United States: no
            minimums, no contracts, real handwriting robots, and 48-hour drop
            times. If a better product came along, we&rsquo;d change our
            recommendation.
          </p>

          <h2 className="font-serif-display text-3xl text-[#1C1C1A] pt-6">
            Who&rsquo;s behind this site
          </h2>
          <p>
            Postmark &amp; Co. is operated by a small independent team of
            direct-response marketers with prior experience running postcard
            campaigns for real estate brokerages, DTC brands, and local
            service businesses. We can be reached at{" "}
            <a
              href="/contact"
              className="text-[#D93829] link-underline"
              data-testid="about-contact-link"
            >
              our contact page
            </a>
            .
          </p>

          <div className="pt-10">
            <a
              href={cta("about")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta"
              className="group inline-flex items-center gap-3 bg-[#D93829] text-white px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#1C1C1A] transition-colors duration-500"
            >
              Try Thanks.io — free first card
              <ArrowUpRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default About;
